export interface EditableTreatment {
  name: string;
  config: string; // empty string = no config
}

const TREATMENT_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9_.-]{0,49}$/;

export function treatmentErrors(treatments: EditableTreatment[]): string[] {
  const errors: string[] = [];
  if (treatments.length === 0) errors.push("at least one treatment is required");
  const seen = new Set<string>();
  for (const t of treatments) {
    if (!TREATMENT_PATTERN.test(t.name)) errors.push(`"${t.name}" is not a valid treatment name`);
    if (t.name === "control") errors.push('"control" is reserved and may not be declared');
    if (seen.has(t.name)) errors.push(`duplicate treatment "${t.name}"`);
    seen.add(t.name);
    if (t.config.trim()) {
      try {
        JSON.parse(t.config);
      } catch {
        errors.push(`"${t.name}" config is not valid JSON`);
      }
    }
  }
  return errors;
}

/** Treatments editor: name + optional JSON config with validation (task brief). */
export function TreatmentsEditor({
  treatments,
  onChange,
  readOnly,
}: {
  treatments: EditableTreatment[];
  onChange: (next: EditableTreatment[]) => void;
  readOnly?: boolean;
}) {
  function update(index: number, patch: Partial<EditableTreatment>) {
    onChange(treatments.map((t, i) => (i === index ? { ...t, ...patch } : t)));
  }
  function remove(index: number) {
    onChange(treatments.filter((_, i) => i !== index));
  }
  function add() {
    onChange([...treatments, { name: `treatment_${treatments.length + 1}`, config: "" }]);
  }

  return (
    <div>
      {treatments.map((t, i) => {
        const nameError = !TREATMENT_PATTERN.test(t.name)
          ? "invalid name"
          : t.name === "control"
            ? '"control" is reserved'
            : null;
        let configError: string | null = null;
        if (t.config.trim()) {
          try {
            JSON.parse(t.config);
          } catch {
            configError = "not valid JSON";
          }
        }
        return (
          <div className="row wrap" key={i} style={{ marginBottom: "var(--space-2)", alignItems: "flex-start" }}>
            <div className="field" style={{ marginBottom: 0, flex: "0 0 200px" }}>
              <label>Treatment name</label>
              <input
                value={t.name}
                disabled={readOnly}
                aria-label={`Treatment name ${i + 1}`}
                onChange={(e) => update(i, { name: e.target.value })}
              />
              {nameError && <span className="error">{nameError}</span>}
            </div>
            <div className="field" style={{ marginBottom: 0, flex: 1 }}>
              <label>Config (JSON, optional)</label>
              <input
                aria-label={`Treatment config ${i + 1}`}
                value={t.config}
                disabled={readOnly}
                placeholder='{"variant":"v2"}'
                onChange={(e) => update(i, { config: e.target.value })}
              />
              {configError && <span className="error">{configError}</span>}
            </div>
            {!readOnly && (
              <button
                type="button"
                className="btn small danger"
                style={{ alignSelf: "flex-end" }}
                onClick={() => remove(i)}
              >
                Remove
              </button>
            )}
          </div>
        );
      })}
      {!readOnly && (
        <button type="button" className="btn small" onClick={add}>
          Add treatment
        </button>
      )}
    </div>
  );
}
